import { ASSET_FAMILY_FACTS } from "./asset-families";
import type { QuestionnaireRefusalParams } from "./operations";
import { isRuleCode, userText } from "./rule-catalog";

/**
 * A refusal's user line: its rule catalog entry's `user` text, filled from
 * the registry facts it names and the typed `params` its raise site set.
 * Undefined for a code the catalog gives no line.
 */
export function refusalLine(
  code: string,
  params?: QuestionnaireRefusalParams,
): string | undefined {
  return isRuleCode(code) ? userText(code, params) : undefined;
}

/** `refusalLine` for a code whose raise site states a rule that has a line. */
export function requiredRefusalLine(
  code: string,
  params?: QuestionnaireRefusalParams,
): string {
  const line = refusalLine(code, params);
  if (line === undefined) {
    throw new Error(`${code}: the rule catalog gives this refusal no user line`);
  }
  return line;
}

/** How the builder names a question to the user, by its place: "Q3". */
export function questionRefLabel(index: number): string {
  return `Q${index + 1}`;
}

/**
 * Refusal lines as a reader sees them, for the builder's Save toast and the
 * chat's reply alike: each distinct line once, in order, with every question
 * it affects ("Affects Q3, Q5."), or none when it names no question.
 */
export function groupRefusalLines(
  entries: ReadonlyArray<{ line: string; questions: ReadonlyArray<string> }>,
): Array<{ line: string; affects: string | null }> {
  return groupRefusalEntries(entries).map(({ line, questions }) => ({
    line,
    affects: questions.length > 0 ? `Affects ${questions.join(", ")}.` : null,
  }));
}

/** Each distinct line once, in order, with every question it affects: the grouping, before any wording. */
export function groupRefusalEntries(
  entries: ReadonlyArray<{ line: string; questions: ReadonlyArray<string> }>,
): Array<{ line: string; questions: Array<string> }> {
  const grouped = new Map<string, Set<string>>();
  for (const { line, questions } of entries) {
    const named = grouped.get(line) ?? new Set<string>();
    grouped.set(line, named);
    questions.forEach((question) => named.add(question));
  }
  return [...grouped].map(([line, named]) => ({ line, questions: [...named] }));
}

const COUNT_PARAMS = ["limit", "ceiling", "count", "excess", "length"] as const;

/**
 * A refusal's structural facts read back from storage or the wire: the asset
 * family and its counts, never text a user wrote (a turn's failure details
 * carry no content). Undefined when it holds none.
 */
export function readStructuralRefusalParams(
  value: unknown,
): QuestionnaireRefusalParams | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const record = value as Record<string, unknown>;
  const params: QuestionnaireRefusalParams = {};
  if (typeof record.family === "string" && record.family in ASSET_FAMILY_FACTS) {
    params.family = record.family as QuestionnaireRefusalParams["family"];
  }
  for (const name of COUNT_PARAMS) {
    const count = record[name];
    if (Number.isInteger(count) && (count as number) >= 0 && (count as number) <= 100_000) {
      params[name] = count as number;
    }
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

/** `{ params }` when `value` holds structural refusal facts, else `{}`. */
export function withStructuralRefusalParams(value: unknown): {
  params?: QuestionnaireRefusalParams;
} {
  const params = readStructuralRefusalParams(value);
  return params ? { params } : {};
}
